import { IterableMap } from "@redotech/react-util/component";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { schemaInstanceExamples } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import {
  EmailBlockType,
  EmailFormat,
  EmailUtmParameters,
  IEmailTemplate,
  ProductSelectionType,
  ScreenSize,
  Section,
} from "@redotech/redo-model/email-builder";
import {
  DESKTOP_WIDTH_PX,
  MOBILE_WIDTH_PX,
} from "@redotech/redo-model/email-builder/email-sizing";
import { ShopifyGraphQLProduct } from "@redotech/redo-model/product";
import MonitorIcon from "@redotech/redo-web/arbiter-icon/monitor-04.svg";
import PhoneIcon from "@redotech/redo-web/arbiter-icon/phone-01.svg";
import { Flex } from "@redotech/redo-web/flex";
import {
  productInfoToRecommendedProduct,
  recommendedProductToStaticRecommendedProduct,
  StaticRecommendedProduct,
} from "@redotech/redo-web/product-recommendations";
import { Text } from "@redotech/redo-web/text";
import * as classNames from "classnames";
import { createContext, memo, useContext, useState } from "react";
import { Button, ButtonSize, ButtonTheme } from "../../button";
import { FormElementDrop } from "../../form-builder/form-element-drop";
import { Icon } from "../../icon";
import SectionToolbar from "../../section-toolbar";
import { EmailBuilderContext } from "./email-builder-context";
import * as css from "./preview.module.css";
import { defaultBlocks, editorPreviews } from "./shared-editor-types";

export function makeObjectId() {
  let result = "";
  const characters = "0123456789abcdef";
  const charactersLength = characters.length;
  for (let i = 0; i < 24; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export const EmailPreview = memo(function EmailPreview({
  template,
  selectedIndex,
  setSelectedIndex,
  selectedColumnIndex,
  setSelectedColumnIndex,
  ...props
}: {
  selectedIndex: number | undefined;
  setSelectedIndex: (index: number | undefined) => void;
  selectedColumnIndex: number | undefined;
  setSelectedColumnIndex: (index: number | undefined) => void;
  template: IEmailTemplate;
  setTemplate: (template: IEmailTemplate) => void;
  format: EmailFormat;
}) {
  const [screenSize, setScreenSize] = useState<ScreenSize>(ScreenSize.DESKTOP);
  const context = useContext(EmailBuilderContext);
  const sections = template.sections;

  const extraPropsMapLoad: LoadState<Record<string, Record<string, any>>> =
    useLoad(async () => {
      if (!context) return {};
      const getProduct = context.getProduct;
      const promises = sections.map((section) =>
        generateExtraProps(section, getProduct),
      );
      const extraProps = await Promise.all(promises);
      const extraPropsMap: Record<string, Record<string, any>> = {};
      sections.forEach((section, index) => {
        extraPropsMap[index] = extraProps[index] ?? {};
      });
      return extraPropsMap;
    }, [sections]);

  if (!context?.template) return "Loading...";

  function addEmailBlock(elementType: EmailBlockType, index: number) {
    const id = makeObjectId();
    const newBlock = { ...defaultBlocks[elementType], blockId: id };
    const newSections = [...template.sections];
    newSections.splice(index, 0, newBlock);
    setSelectedColumnIndex(undefined);
    setSelectedIndex(index);
    props.setTemplate({ ...template, sections: newSections });
  }

  const footerCount = sections.filter(
    (section) => section.type === EmailBlockType.FOOTER,
  ).length;

  return (
    <Flex dir="column" gap="none" grow={1.5}>
      <Flex
        bgColor="primary"
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        justify="space-between"
        pb="xl"
        pt="3xl"
        px="5xl"
        w="full"
      >
        <Text fontWeight="bold">Preview</Text>
        <Button
          onClick={() =>
            setScreenSize(
              screenSize === ScreenSize.MOBILE
                ? ScreenSize.DESKTOP
                : ScreenSize.MOBILE,
            )
          }
          p="md"
          size={ButtonSize.MICRO}
          theme={ButtonTheme.GHOST}
        >
          <Icon
            arbiterIconSvg={
              screenSize === ScreenSize.MOBILE ? PhoneIcon : MonitorIcon
            }
            color="ghost"
            style={{ width: 19, height: 18 }}
          />
        </Button>
      </Flex>
      <Flex
        height="full"
        justify="center"
        onClick={() => setSelectedIndex(undefined)}
        overflowY="auto"
        py="7xl"
        style={{ backgroundColor: template.contentBackgroundColor }}
      >
        <Flex
          className={classNames(css.emailViewer)}
          dir="column"
          gap="none"
          mx="md"
          style={{ width: widths[screenSize] }}
        >
          <ScreenSizeContext.Provider value={screenSize}>
            <IterableMap
              items={sections.values()}
              // The key needs to be both unique and change whenever a section is ordered.
              // In other words, blockId or index by themselves cause bugs, therefore we use both.
              // Bizarrely, some email templates have sections with the same blockId, so this also prevents bugs from that.
              keyFn={(section, index) => `${section.blockId}-${index}`}
            >
              {(section, index) => {
                const BlockEditor = editorPreviews[section.type];
                const preview = (
                  <BlockEditor
                    index={index}
                    onSelectElement={(index) => {
                      if (index !== selectedIndex) {
                        setSelectedColumnIndex(undefined);
                      }
                      setSelectedIndex(index);
                    }}
                    sectionToolbar={
                      <SectionToolbar<Section>
                        canDuplicate={false}
                        disableDelete={
                          section.type === EmailBlockType.FOOTER &&
                          template.category === "Marketing" &&
                          footerCount <= 1
                        }
                        index={index}
                        open={selectedIndex === index}
                        sections={sections}
                        setModified={() => {}}
                        setSections={(newSections) => {
                          props.setTemplate({
                            ...template,
                            sections: newSections,
                          });
                        }}
                        setSelected={(newIndex) => {
                          setSelectedIndex(
                            newIndex === undefined
                              ? undefined
                              : Number(newIndex),
                          );
                        }}
                        totalSections={sections.length}
                      />
                    }
                    selected={selectedIndex === index}
                    selectedColumnIndex={selectedColumnIndex}
                    setElement={(newElement) =>
                      props.setTemplate({
                        ...template,
                        sections: sections.map((s, i) =>
                          i === index ? newElement : s,
                        ),
                      })
                    }
                    setSelectedColumnIndex={setSelectedColumnIndex}
                    widget={{
                      element: section,
                      format: props.format,
                      template: template,
                      isBuilder: true,
                      schemaInstance:
                        schemaInstanceExamples[template.schemaType],
                      utm: new EmailUtmParameters("preview"),
                      team: context.team,
                      extraProps: extraPropsMapLoad.value?.[index] ?? {},
                    }}
                  />
                );
                return (
                  <>
                    {preview}
                    {index !== sections.length - 1 && (
                      <FormElementDrop
                        elementDropped={(element) =>
                          addEmailBlock(element, index + 1)
                        }
                        hideUntilDragging
                        possibleValues={Object.values(EmailBlockType)}
                      />
                    )}
                  </>
                );
              }}
            </IterableMap>
          </ScreenSizeContext.Provider>
        </Flex>
      </Flex>
    </Flex>
  );
});

export enum SectionStatus {
  NORMAL = 0,
  SELECTED = 1,
}

export const ScreenSizeContext = createContext<ScreenSize>(ScreenSize.DESKTOP);

const widths: Record<ScreenSize, number> = {
  [ScreenSize.MOBILE]: MOBILE_WIDTH_PX,
  [ScreenSize.DESKTOP]: DESKTOP_WIDTH_PX,
};

export async function generateExtraProps(
  section: Section,
  getProduct: (id: string) => Promise<ShopifyGraphQLProduct | undefined>,
) {
  switch (section.type) {
    case EmailBlockType.INTERACTIVE_CART: {
      if (section.productSelectionType !== ProductSelectionType.STATIC)
        return undefined;
      const promises =
        section.manuallySelectedProducts?.map(async (dehydratedProduct) => {
          if (!dehydratedProduct) return null;
          const productInfo = await getProduct(dehydratedProduct.productId);
          const recommendedProduct = productInfo
            ? productInfoToRecommendedProduct(productInfo)
            : null;
          return recommendedProduct
            ? recommendedProductToStaticRecommendedProduct(
                section,
                recommendedProduct,
                { alreadyInCart: false },
              )
            : null;
        }) ?? [];
      const products = (await Promise.all(promises)).filter(
        (product): product is StaticRecommendedProduct => product !== null,
      );
      return { staticRecommendedProducts: products };
    }
  }
  return {};
}
